import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ROLE_LABEL } from "../lib/constants";
import { Plus, Check, X } from "lucide-react";

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [resetReqs, setResetReqs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "customer", customer_id: "" });

  const load = useCallback(async () => {
    const [u, p, c, r] = await Promise.all([
      api.get("/users"),
      api.get("/users/pending"),
      api.get("/customers"),
      api.get("/users/reset-requests"),
    ]);
    setUsers(u.data);
    setPending(p.data);
    setCustomers(c.data);
    setResetReqs(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "customer", customer_id: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { is_active: !u.is_active }); toast.success("Updated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const approve = async (uid) => {
    try { await api.post(`/users/${uid}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const reject = async (uid) => {
    const reason = prompt("Optional rejection reason:") ?? "";
    try { await api.post(`/users/${uid}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const activeUsers = users.filter((u) => u.approval_status !== "pending" && u.approval_status !== "rejected" && u.is_active);
  const deactivatedUsers = users.filter((u) => u.approval_status !== "pending" && u.approval_status !== "rejected" && !u.is_active);
  const rejectedUsers = users.filter((u) => u.approval_status === "rejected");

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="heading text-3xl font-bold text-slate-900">Users</h1>
          <p className="text-slate-500 mt-1">
            {activeUsers.length} active · {deactivatedUsers.length} deactivated · {rejectedUsers.length} rejected
            {pending.length ? ` · ${pending.length} pending approval` : ""}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-600 hover:bg-orange-700" data-testid="new-user-button"><Plus className="w-4 h-4 mr-2" />New User</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="inward">Inward Team</SelectItem>
                    <SelectItem value="quality">Quality Team</SelectItem>
                    <SelectItem value="accounts">Accounts Team</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "customer" && (
                <div>
                  <Label>Linked Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Choose customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} className="bg-orange-600 hover:bg-orange-700" data-testid="create-user-submit">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue={resetReqs.length > 0 ? "reset" : "pending"}>
        <TabsList>
          <TabsTrigger value="reset" data-testid="tab-reset-requests">
            Reset Requests
            {resetReqs.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-red-600 text-white font-semibold">
                {resetReqs.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending Approvals
            {pending.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-orange-600 text-white font-semibold">
                {pending.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="active" data-testid="tab-all-users">Active Users</TabsTrigger>
          <TabsTrigger value="deactivated" data-testid="tab-deactivated">
            Deactivated
            {deactivatedUsers.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-stone-500 text-white font-semibold">
                {deactivatedUsers.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">
            Rejected Requests
            {rejectedUsers.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-red-600 text-white font-semibold">
                {rejectedUsers.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="reset">
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Requested</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resetReqs.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No pending password reset requests.</TableCell></TableRow>
                ) : resetReqs.map((r) => (
                  <TableRow key={r.id} data-testid={`reset-req-row-${r.email}`}>
                    <TableCell className="text-xs text-slate-500">{new Date(r.requested_at).toLocaleString()}</TableCell>
                    <TableCell className="font-medium">{r.name || "—"}</TableCell>
                    <TableCell>{r.email}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={async () => {
                        const np = prompt(`Issue a temporary password for ${r.email}. User will be forced to change it on next login:`);
                        if (!np) return;
                        if (np.length < 6) { toast.error("Min 6 characters"); return; }
                        try { await api.post(`/users/${r.user_id}/reset-password`, { new_password: np }); toast.success("Temporary password issued"); load(); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
                      }} data-testid={`issue-pw-${r.email}`}>
                        Issue Temp Password
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Registered</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No pending registrations.</TableCell></TableRow>
                ) : pending.map((u) => (
                  <TableRow key={u.id} data-testid={`pending-row-${u.email}`}>
                    <TableCell className="text-xs text-slate-500">{new Date(u.created_at).toLocaleString()}</TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <div>{u.customer?.name || "—"}</div>
                      <div className="text-xs text-slate-500">{u.customer?.customer_code}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{u.customer?.gstin || "—"}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => approve(u.id)} data-testid={`approve-${u.email}`}>
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => reject(u.id)} data-testid={`reject-${u.email}`}>
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="active">
          <Card>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {activeUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No active users.</TableCell></TableRow>
                ) : activeUsers.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABEL[u.role]}</Badge></TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-700 border-green-200" variant="outline">Active</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button variant="outline" size="sm" onClick={async () => {
                        const np = prompt(`Set a temporary password for ${u.email}. User will be forced to change it on next login:`);
                        if (!np) return;
                        if (np.length < 6) { toast.error("Min 6 characters"); return; }
                        try { await api.post(`/users/${u.id}/reset-password`, { new_password: np }); toast.success("Temporary password issued"); load(); }
                        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
                      }} data-testid={`reset-pw-${u.email}`}>
                        Reset Password
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(u)} data-testid={`toggle-${u.email}`}>
                        Deactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="deactivated">
          <Card>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {deactivatedUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No deactivated users.</TableCell></TableRow>
                ) : deactivatedUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`deactivated-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABEL[u.role]}</Badge></TableCell>
                    <TableCell>
                      <Badge className="bg-stone-200 text-stone-700 border-stone-300" variant="outline">Deactivated</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => toggleActive(u)} data-testid={`toggle-${u.email}`}>
                        Reactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="rejected">
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Rejected On</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Rejected By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rejectedUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">No rejected requests.</TableCell></TableRow>
                ) : rejectedUsers.map((u) => (
                  <TableRow key={u.id} data-testid={`rejected-row-${u.email}`}>
                    <TableCell className="text-xs text-slate-500">{u.rejected_at ? new Date(u.rejected_at).toLocaleString() : "—"}</TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABEL[u.role]}</Badge></TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-xs truncate">{u.rejection_reason || "—"}</TableCell>
                    <TableCell className="text-sm">{u.rejected_by || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
